import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { DataTablePagination } from '@/components/DataTablePagination';
import { MonthRange } from '@/components/MonthControls';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { affiliatesApi, reportsApi } from '@/lib/api/endpoints';
import { fmtDay, fmtInt, fmtMoney } from '@/lib/format';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 50;

export function AffiliatesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const months = useQuery({ queryKey: ['months'], queryFn: reportsApi.months, staleTime: 60_000 });
  const available = months.data ?? [];
  const from = params.get('from') ?? available[0] ?? '';
  const to = params.get('to') ?? available[available.length - 1] ?? '';
  const search = params.get('q') ?? '';
  const only = params.get('only') === 'all' ? 'all' : 'active';
  const sortBy = params.get('sort') ?? 'netSales';
  const sortDir = params.get('dir') ?? 'desc';
  const page = Number(params.get('page') ?? 1);
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const query = {
    from: from || undefined,
    to: to || undefined,
    search: search || undefined,
    only,
    sortBy,
    sortDir,
    page,
    pageSize: PAGE_SIZE,
  };
  const q = useQuery({
    queryKey: ['affiliates', query],
    queryFn: () => affiliatesApi.list(query),
    enabled: !months.isLoading,
    placeholderData: (p) => p,
  });

  return (
    <div>
      <PageHeader
        title="Afiliados"
        description="Todos os afiliados que apareceram nos relatórios, com os totais do período"
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <MonthRange
          months={available}
          from={from}
          to={to}
          onChange={(f, t) => set({ from: f, to: t })}
        />
        <div className="space-y-1">
          <Label className="text-xs">Ordenar por</Label>
          <Select
            value={`${sortBy}:${sortDir}`}
            onValueChange={(v) => {
              const [s, d] = v.split(':');
              set({ sort: s, dir: d });
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="netSales:desc">Maior líquido</SelectItem>
              <SelectItem value="grossSales:desc">Maior bruto</SelectItem>
              <SelectItem value="orders:desc">Mais pedidos</SelectItem>
              <SelectItem value="lastPeriod:desc">Venda mais recente</SelectItem>
              <SelectItem value="name:asc">Nome (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-56 flex-1 space-y-1">
          <Label className="text-xs" htmlFor="aff-q">
            Buscar
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="aff-q"
              placeholder="Nome do afiliado"
              className="pl-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && set({ q: searchInput.trim() })}
              onBlur={() => searchInput.trim() !== search && set({ q: searchInput.trim() })}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch
            id="aff-only"
            checked={only === 'active'}
            onCheckedChange={(v) => set({ only: v ? undefined : 'all' })}
          />
          <Label htmlFor="aff-only" className="text-xs">
            Só com faturamento
          </Label>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : q.data?.items.length ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Afiliado</TableHead>
                  <TableHead className="text-right">Bruto</TableHead>
                  <TableHead className="text-right">Líquido</TableHead>
                  <TableHead className="text-right">Comissões</TableHead>
                  <TableHead className="text-right">Pedidos</TableHead>
                  <TableHead className="text-right">Meses</TableHead>
                  <TableHead>Primeiro · último</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.items.map((a) => (
                  <TableRow
                    key={a.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/afiliados/${a.id}?from=${from}&to=${to}`)}
                  >
                    <TableCell>
                      <p className="font-medium">
                        {a.name}
                        {a.aliasCount > 0 && (
                          <Badge
                            variant="outline"
                            className="ml-2 align-middle text-[10px]"
                            title="Outros nomes dos relatórios apontam para este cadastro"
                          >
                            +{a.aliasCount} nome(s)
                          </Badge>
                        )}
                      </p>
                      {a.notes && (
                        <p className="truncate text-xs text-muted-foreground">{a.notes}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(a.totals.grossSales)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right font-medium tabular-nums',
                        a.totals.netSales < 0 && 'text-destructive',
                      )}
                    >
                      {fmtMoney(a.totals.netSales)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(a.totals.commissionsNet)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtInt(a.totals.orders)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{a.monthsActive}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {a.firstPeriod ? `${fmtDay(a.firstPeriod)} · ${fmtDay(a.lastPeriod)}` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DataTablePagination
              page={q.data.page}
              pageSize={q.data.pageSize}
              total={q.data.total}
              onPageChange={(p) => set({ page: String(p) })}
            />
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Users}
              title="Nenhum afiliado"
              description={
                search
                  ? 'Tente outro termo.'
                  : only === 'active'
                    ? 'Nenhum afiliado faturou no período. Desligue "Só com faturamento" para ver todos.'
                    : 'Importe relatórios para ver os afiliados.'
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
